"use client";

import {
  isHrtStorefrontBookable,
  isOrgExperityBookable,
} from "@/app/directories/shop/lib/storefrontLogicHelper";
import { PriceExplainabilityRenderer } from "@/components/PriceExplainability/PriceExplainabilityRenderer";
import type { Hrt } from "@/lib/graphql/generated-gql";
import { useInsuranceGatedToggle } from "@frontend/components/InsuranceConnector/insurance-connector-provider";
import { partitionHrts } from "@frontend/components/InsuranceStepper/InsuranceStepper.helper";
import type { StorefrontProductPageHrtType } from "@frontend/components/InsuranceStepper/InsuranceStepper.type";
import { EligibilityButton } from "@frontend/components/eligibility/EligibilityButton";
import { CalculatingPricesPill } from "@frontend/components/priceToggle/CalculatingPricesPill";
import { PriceToggleSymbol } from "@frontend/components/priceToggle/priceToggleSymbol";
import { getAllEnabledOrgConfigFlagsByOrgId } from "@frontend/lib/actions/actions";
import { getOrganizationAssetsForOrg } from "@frontend/lib/actions/config-actions";
import { useTranslations } from "@frontend/lib/next-intl-typed";
import { useBoundStore } from "@frontend/lib/stores/globalStore";
import type { PricingMapEntries } from "@frontend/lib/stores/slices/pricingMapSlice";
import { Label } from "@frontend/shared/components/ui/label";
import { Button as MovingBorderButton } from "@frontend/shared/components/ui/moving-border";
import { PriceSwitch } from "@frontend/shared/components/ui/switch";
import { useIsFetching, useQuery } from "@tanstack/react-query";
import Link from "next/link";
import { HrtOptionsGrid } from "./HrtOptionsGrid";
import { InitialFollowUpSelector } from "./InitialFollowUpSelector";
import { type HrtOption, useProduct } from "./ProductContext";

interface HrtSelectorProps {
  clientSlug: string;
  orgId: number;
  product: {
    name: string;
    displayMetadata?: {
      imageUrl: string;
      preDiscount: string;
    };
    productToHrt: Array<{
      hrtId: number;
      product_component_type: string;
      hrt: StorefrontProductPageHrtType;
    }>;
  };
}

export function HrtSelector({ clientSlug, orgId, product }: HrtSelectorProps) {
  const t = useTranslations("Shop");
  const tStorefront = useTranslations("StorefrontInsurance");
  const {
    selectedHrtId,
    setSelectedHrtId,
    defaultOptions,
    initialRequiredOptions,
    selectedInitialRequiredHrtId,
    setSelectedInitialRequiredHrtId,
  } = useProduct();
  const pricingMap = useBoundStore.use.pricingMapEntries();
  const setPricingMap = useBoundStore.use.setPricingMap();
  const storefrontPaymentMode = useBoundStore.use.storefrontPaymentMode();
  const isPendingPricesMode = useBoundStore.use.isPendingPricesMode();

  const { data: configFlags } = useQuery({
    queryKey: ["configFlags", orgId],
    queryFn: () => getAllEnabledOrgConfigFlagsByOrgId(orgId),
    enabled: !!orgId,
  });

  const { data: organizationAssets } = useQuery({
    queryKey: ["organizationAssets", orgId],
    queryFn: () => getOrganizationAssetsForOrg(orgId),
    enabled: !!orgId,
  });

  const handlePriceModeChange = (checked: boolean) => {
    const newPricingMap = { ...pricingMap };
    for (const option of defaultOptions) {
      const hrtId = option.hrt.hrtId;
      const isInsurable = option.hrt.hrtPaymentType.some(
        (paymentType) => paymentType.paymentType === "INSURED",
      );
      if (newPricingMap[hrtId]) {
        newPricingMap[hrtId] = {
          ...newPricingMap[hrtId],
          mode: checked && isInsurable ? "INSURED" : "CASH",
        };
      }
    }
    setPricingMap(newPricingMap);
  };

  const { hrtIds, insurableHrtIds } = partitionHrts(
    defaultOptions.flatMap((option) => option.hrt),
  );

  // Prefer the persisted pricingMap mode (set by user toggles), and fall
  // back to `storefrontPaymentMode` when no entries have been written yet.
  // In pending-prices mode `storefrontPaymentMode` is "INSURED", so the
  // toggle naturally defaults to ON on first render — but the user is
  // still free to flip it off to see self-pay prices.
  const pricingMapIsInsured = defaultOptions.some(
    (option) => pricingMap[option.hrt.hrtId]?.mode === "INSURED",
  );
  const hasAnyPricingMode = defaultOptions.some(
    (option) => !!pricingMap[option.hrt.hrtId]?.mode,
  );
  const isInsuredMode = hasAnyPricingMode
    ? pricingMapIsInsured
    : storefrontPaymentMode === "INSURED";

  const pricingStatus = useBoundStore.use.pricingStatus();
  const isFetchingPrice = !!useIsFetching({ queryKey: ["updatePrices"] });
  const pricesLoaded = pricingStatus === "SUCCESS" && !isFetchingPrice;
  // Check if product has an INITIAL_REQUIRED option
  const hasInitialRequired = initialRequiredOptions.some(
    (opt) => opt.product_component_type === "INITIAL_REQUIRED",
  );

  const selectedHrt = defaultOptions.find(
    (option) => option.hrt.hrtId === selectedHrtId,
  );

  const selectedInitialRequiredHrt = initialRequiredOptions.find(
    (option) => option.hrt.hrtId === selectedInitialRequiredHrtId,
  );

  const {
    createGatedHandler,
    isInsuranceConnected,
    canUseInsuredPricing,
    insuranceState,
    sendInsurance,
  } = useInsuranceGatedToggle();
  // Pending-prices mode = unverified insurance was already submitted. Treat
  // that like a fully-connected row so we hide Connect Insurance, keep the
  // Book Now CTA in its primary state, and surface the pending-prices copy.
  const isInsuranceEffectivelyConnected =
    isInsuranceConnected || isPendingPricesMode;
  // Only surface the INSURED ↔ self-pay switch when the user actually has
  // a verified-active insurance OR is in pending-prices mode. For
  // inactive / out-of-network / unverified-no-flag connections we keep the
  // saved card visible (so the user can disconnect it) but lock the
  // pricing UI to self-pay — flipping the switch otherwise would render
  // $0 / $NaN because there's no INSURED price data on the wire.
  const showInsuredPriceToggle =
    canUseInsuredPricing && insurableHrtIds.length > 0;
  const orgName = insuranceState.context.orgName;
  const eligibilityCheckResult = useBoundStore.use.eligibilityCheckResult();
  const hasFailedEligibility =
    !!configFlags?.storefront_offboard_consumers &&
    eligibilityCheckResult.result === "failed";
  const showConnectInsurance =
    !isInsuranceEffectivelyConnected &&
    insurableHrtIds.length > 0 &&
    !hasFailedEligibility;

  const handlePriceModeChangeWithInsuranceCheck = createGatedHandler(
    handlePriceModeChange,
  );

  const isBookable =
    isHrtStorefrontBookable(selectedHrt?.hrt, configFlags) ||
    isHrtStorefrontBookable(selectedInitialRequiredHrt?.hrt, configFlags);

  const isExperityBookable = isOrgExperityBookable(orgId, configFlags);

  return (
    <div className="flex flex-col min-h-fit gap-6 w-full">
      {hasInitialRequired && (
        <div className="flex flex-col min-h-fit gap-6 w-full">
          <div className="flex justify-between items-center mb-[-12px]">
            <p className="font-light font-mono text-sm text-[#767676]">
              {t("VisitType").toUpperCase()}
            </p>
          </div>
          <InitialFollowUpSelector
            initialOptions={initialRequiredOptions}
            selectedHrtId={selectedInitialRequiredHrtId}
            onHrtSelect={setSelectedInitialRequiredHrtId}
            pricingMap={pricingMap}
            calculatePrice={calculatePrice}
          />
        </div>
      )}

      {isFetchingPrice ? (
        <CalculatingPricesPill />
      ) : (
        <div className="flex flex-col min-h-fit gap-6 w-full">
          <div className="flex justify-between items-center mb-[-12px]">
            {defaultOptions.length > 1 && (
              <p className="font-light font-mono text-sm text-[#767676]">
                {t("Includes")}
              </p>
            )}
            {showInsuredPriceToggle && (
              <div className="flex items-center space-x-2">
                <Label className="text-sm font-medium">
                  {isInsuredMode ? t("InsuredPrice") : t("SelfPay")}
                </Label>

                <PriceSwitch
                  checked={isInsuredMode}
                  onCheckedChange={handlePriceModeChangeWithInsuranceCheck}
                >
                  <PriceToggleSymbol checked={isInsuredMode} />
                </PriceSwitch>
              </div>
            )}
          </div>
          <HrtOptionsGrid
            options={defaultOptions}
            selectedHrtId={selectedHrtId}
            onHrtSelect={setSelectedHrtId}
            pricingMap={pricingMap}
            calculatePrice={calculatePrice}
            isInitialRequired={false}
            noInsuranceProcedures={!!configFlags?.no_insured_procedures}
            secondaryColor={organizationAssets?.secondaryColor ?? "#000000"}
          />
        </div>
      )}

      <PriceExplainabilityRenderer
        hrt={
          {
            ...product.productToHrt[0].hrt,
            orgId: orgId,
          } as Hrt
        }
      />

      <div className="flex flex-col min-h-fit gap-6 w-full items-center">
        {showConnectInsurance && (
          <MovingBorderButton
            hoverAnimation
            icon
            iconInline
            borderRadius="9999px"
            duration={8000}
            containerClassName="w-full h-[70px]"
            className="bg-black text-white border-black px-6 text-lg font-medium"
            onClick={() => sendInsurance({ type: "OPEN" })}
          >
            {tStorefront("ConnectInsurance")}
          </MovingBorderButton>
        )}
        <EligibilityButton
          link={`/${clientSlug}/schedule?hrtId=${
            selectedInitialRequiredHrtId
              ? `${selectedInitialRequiredHrtId},`
              : ""
          }${selectedHrtId}`}
          orgId={orgId}
          classNames="w-full h-[64px] flex items-center justify-center"
        >
          <div className="flex justify-center w-full">
            {isBookable ? (
              <Link
                className={`w-full rounded-full h-[64px] justify-center items-center flex text-lg transition-all duration-200 ${
                  showConnectInsurance
                    ? "bg-white border hover:!bg-black hover:!text-white hover:!border-black"
                    : "bg-black text-white hover:opacity-85"
                }`}
                style={
                  showConnectInsurance
                    ? {
                        color: organizationAssets?.primaryColor ?? "#000000",
                        borderColor:
                          organizationAssets?.primaryColor ?? "#000000",
                      }
                    : {
                        backgroundColor:
                          organizationAssets?.primaryColor ?? "#000000",
                      }
                }
                href={`/${clientSlug}/schedule?hrtId=${
                  selectedInitialRequiredHrtId
                    ? `${selectedInitialRequiredHrtId},`
                    : ""
                }${selectedHrtId}`}
              >
                {t("BuyNow")}
              </Link>
            ) : isExperityBookable ? (
              <Link
                className={`w-full rounded-full h-[64px] justify-center items-center flex text-lg transition-all duration-200 ${
                  showConnectInsurance
                    ? "bg-white border hover:!bg-black hover:!text-white hover:!border-black"
                    : "bg-black text-white hover:opacity-85"
                }`}
                style={
                  showConnectInsurance
                    ? {
                        color: organizationAssets?.primaryColor ?? "#000000",
                        borderColor:
                          organizationAssets?.primaryColor ?? "#000000",
                      }
                    : {
                        backgroundColor:
                          organizationAssets?.primaryColor ?? "#000000",
                      }
                }
                href={`/${clientSlug}/schedule?hrtId=${
                  selectedInitialRequiredHrtId
                    ? `${selectedInitialRequiredHrtId},`
                    : ""
                }${selectedHrtId}`}
              >
                {t("BuyNow")}
              </Link>
            ) : (
              <Link
                className={`w-full rounded-full h-[64px] justify-center items-center flex text-lg transition-all duration-200 ${
                  showConnectInsurance
                    ? "bg-white border hover:!bg-black hover:!text-white hover:!border-black"
                    : "bg-black text-white hover:opacity-85"
                }`}
                style={
                  showConnectInsurance
                    ? {
                        color: organizationAssets?.primaryColor ?? "#000000",
                        borderColor:
                          organizationAssets?.primaryColor ?? "#000000",
                      }
                    : {
                        backgroundColor:
                          organizationAssets?.primaryColor ?? "#000000",
                      }
                }
                href={`/${clientSlug}/review?hrtId=${
                  selectedInitialRequiredHrtId
                    ? `${selectedInitialRequiredHrtId},`
                    : ""
                }${selectedHrtId}`}
              >
                {t("BuyNow")}
              </Link>
            )}
          </div>
        </EligibilityButton>
        {/* Only surface the pending-prices copy when the insured toggle is
            on — if the user flipped to self-pay they're looking at the cash
            price and the pending-review messaging doesn't apply. */}
        {isPendingPricesMode && isInsuredMode && insurableHrtIds.length > 0 && (
          <p className="w-full text-sm text-center text-gray-600">
            {tStorefront("OrgWillReviewCoverage", { orgName })}
          </p>
        )}
      </div>
    </div>
  );
}

// Helper function to calculate price
export function calculatePrice(
  option: HrtOption,
  pricingMap: PricingMapEntries,
) {
  const priceEntry = pricingMap[option.hrt.hrtId];

  const basePrice =
    priceEntry?.mode === "INSURED"
      ? priceEntry?.priceData.INSURED?.price
      : priceEntry?.priceData.CASH?.price;

  if (basePrice == null) {
    return Number.NaN;
  }

  if (
    option.product_component_type === "INITIAL_REQUIRED" &&
    option.defaultHrt
  ) {
    const defaultPriceEntry = pricingMap[option.defaultHrt.hrtId];
    const defaultPrice =
      defaultPriceEntry?.mode === "INSURED"
        ? defaultPriceEntry?.priceData.INSURED?.price
        : defaultPriceEntry?.priceData.CASH?.price;

    if (!defaultPrice) {
      return Number.NaN;
    }
    return basePrice + defaultPrice;
  }

  return basePrice;
}
